import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, Chip, IconButton, Divider, Card, CardContent, Grid, Avatar } from '@mui/material';
import z from 'zod';
import '../../css/custom-scrollbar.css'
import { FaLock } from "react-icons/fa";


// Using text/emoji icons instead of @mui/icons-material

const SaveIcon = () => <span>💾</span>;
const CloseIcon = () => <span>❌</span>;
const ReceiptIcon = () => <span>🧾</span>;
const BankIcon = () => <span>🏦</span>;
const TrendingUpIcon = () => <span>📈</span>;
const EditIcon = () => <span>✏️</span>;
const CheckIcon = () => <span>✅</span>;


const orderSchema = z.object({
  amount: z.string(),
  order_id: z.string()
});

const schema = z.object({
  id: z.string(),
  retailer_id: z.string(),
  settle_id: z.string(),
  order_id: z.string(),
  remaining_amount: z.string(),
  status: z.string(),
  approved_by: z.string(),
  approved_at: z.string(),
  updated_at: z.string(),
  created_at: z.string()
});

const ViewRemittance = ({ isopen, onClose, data }) => {
  const [orderData, setOrderData] = useState([]);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  // const [editingRow, setEditingRow] = useState(null);

  useEffect(() => {
    if (data && data.order_id) {
      try {
        const parsedOrders = JSON.parse(data.order_id);
        setOrderData(parsedOrders);

        const initialFormData = {};
        parsedOrders.forEach(order => {
          initialFormData[order.order_id] = order.amount;
        });
        setFormData(initialFormData);
      } catch (error) {
        console.error('Error parsing order data:', error);
        setOrderData([]);
      }
    }
  }, [data]);

  const handleClose = () => {
    onClose();
    setErrors({});
    // setEditingRow(null);
  };

  const handleAmountChange = (orderId, value) => {
    setFormData(prev => ({
      ...prev,
      [orderId]: value
    }));

    if (errors[orderId]) {
      setErrors(prev => ({
        ...prev,
        [orderId]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    Object.keys(formData).forEach(orderId => {
      const amount = formData[orderId];
      if (!amount || amount.trim() === '') {
        newErrors[orderId] = 'Amount is required';
      } else if (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
        newErrors[orderId] = 'Amount must be a valid positive number';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      const updatedOrderData = orderData.map(order => ({
        ...order,
        amount: formData[order.order_id]
      }));

      handleClose();
    }
  };

  const calculateTotal = () => {
    return Object.values(formData).reduce((total, amount) => {
      return total + (parseFloat(amount) || 0);
    }, 0).toFixed(2);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return { color: 'default', bg: 'linear-gradient(135deg, #9e9e9e 0%, #616161 100%)' };
      case 'process':
        return { color: 'warning', bg: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' };
      case 'complete':
        return { color: 'success', bg: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)' };
      default:
        return { color: 'default', bg: 'linear-gradient(135deg, #b0bec5 0%, #78909c 100%)' }; // Light blue-grey fallback
    }
  };

  if (!data) return null;

  const statusStyle = getStatusColor((data.status?.toLowerCase()));

  return (
    <Dialog
      open={isopen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          background: 'linear-gradient(135deg,rgb(255, 255, 255) 0%, #f8fafc 100%)'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          color: 'white',
          p: 2.5,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
          }
        }}
      >
        {/* Floating Close Button */}
        <IconButton
          onClick={onClose} // make sure this function is defined
          size="small"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            color: 'white',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box display="flex" alignItems="center" justifyContent="space-between" position="relative" zIndex={1}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar sx={{
              width: 36,
              height: 36,
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <ReceiptIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="600" fontSize="1rem">
                Remittance Management
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.75rem' }}>
                Settlement ID: {data.settle_id}
              </Typography>
            </Box>
          </Box>

          <Chip
            label={data.status?.toLowerCase()}
            size="small"
            sx={{
              background: statusStyle.bg,
              color: 'white',
              fontWeight: '600',
              fontSize: '0.75rem',
              height: '28px',
              textTransform: 'capitalize',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)' }}>
        <Box sx={{ p: 2.5 }}>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(139, 92, 246, 0.25)',
                minHeight: '80px'
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{
                      width: 32,
                      height: 32,
                      background: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <BankIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.7rem' }}>
                        Retailer ID
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="600" fontSize="0.875rem">
                        #{data.retailer_id}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                color: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(236, 72, 153, 0.25)',
                minHeight: '80px'
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{
                      width: 32,
                      height: 32,
                      background: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <TrendingUpIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.7rem' }}>
                        Remaining Amount
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="600" fontSize="0.875rem">
                        ₹{parseFloat(data.remaining_amount).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                color: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(6, 182, 212, 0.25)',
                minHeight: '80px'
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{
                      width: 32,
                      height: 32,
                      background: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <CheckIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.7rem' }}>
                        Total Orders
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="600" fontSize="0.875rem">
                        {orderData.length}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(245, 158, 11, 0.25)',
                minHeight: '80px'
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{
                      width: 32,
                      height: 32,
                      background: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <ReceiptIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.7rem' }}>
                        Total Amount
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="600" fontSize="0.875rem">
                        ₹{parseFloat(calculateTotal()).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{
            my: 2.5,
            background: 'linear-gradient(90deg, transparent, #8b5cf6, transparent)',
            height: '1px',
            border: 'none'
          }} />

          {/* Status-based Notice */}
          {(data.status?.toLowerCase()) !== 'pending' && (
            <Box sx={{
              mb: 3,
              p: 2,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '1px solid #f59e0b'
            }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                {/* <InfoIcon sx={{ color: '#d97706', fontSize: '1.25rem' }} /> */}
                <Typography variant="body2" color="#92400e" fontWeight="500" fontSize="0.8rem">
                  Amount editing is disabled for {(data.status?.toLowerCase())} settlements. Only pending settlements allow modifications.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Enhanced Table */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{
              mb: 2,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              fontWeight: '600',
              fontSize: '1.1rem'
            }}>
              Order Details & Amounts
            </Typography>

            <TableContainer component={Paper} elevation={0} sx={{
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 25px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    '& .MuiTableCell-head': {
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '0.8rem',
                      py: 1.5
                    }
                  }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ReceiptIcon fontSize="small" />
                        Order ID
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TrendingUpIcon fontSize="small" />
                        Amount (₹)
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        {/* {(data.status?.toLowerCase()) === 'pending' ? <EditIcon fontSize="small" /> : <FaLock fontSize="small" style={{ color: '#f87171' }} />} */}
                        Status
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderData.map((order, index) => (
                    <TableRow
                      key={index}
                      hover={(data.status?.toLowerCase()) === 'pending'}
                      sx={{
                        '&:hover': (data.status?.toLowerCase()) === 'pending' ? {
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.04) 0%, rgba(124, 58, 237, 0.04) 100%)',
                          transform: 'scale(1.001)',
                          transition: 'all 0.2s ease'
                        } : {},
                        '&:nth-of-type(even)': {
                          backgroundColor: 'rgba(139, 92, 246, 0.02)'
                        },
                        opacity: (data.status?.toLowerCase()) === 'pending' ? 1 : 0.7
                      }}
                    >
                      <TableCell sx={{ py: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar sx={{
                            width: 28,
                            height: 28,
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                            fontSize: '0.7rem',
                            fontWeight: '600'
                          }}>
                            {index + 1}
                          </Avatar>
                          <Typography variant="body2" fontFamily="monospace" fontWeight="500" fontSize="0.8rem">
                            {order.order_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <TextField
                          value={formData[order.order_id] || ''}
                          onChange={(e) => handleAmountChange(order.order_id, e.target.value)}
                          error={!!errors[order.order_id]}
                          helperText={errors[order.order_id]}
                          size="small"
                          type="number"
                          disabled={(data.status?.toLowerCase()) !== 'pending'}
                          inputProps={{
                            min: "0",
                            step: "0.01",
                            style: {
                              textAlign: 'right',
                              fontWeight: '500',
                              fontSize: '0.8rem'
                            }
                          }}
                          sx={{
                            width: '120px',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              background: (data.status?.toLowerCase()) === 'pending'
                                ? 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                                : '#f1f5f9',
                              fontSize: '0.8rem',
                              '&:hover fieldset': {
                                borderColor: (data.status?.toLowerCase()) === 'pending' ? '#8b5cf6' : '#cbd5e1',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#8b5cf6',
                                borderWidth: '2px',
                                boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)'
                              },
                              '&.Mui-disabled': {
                                background: '#f1f5f9',
                                '& fieldset': {
                                  borderColor: '#cbd5e1'
                                }
                              }
                            },
                            '& .MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: '#64748b',
                              color: '#64748b'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.5 }}>
                        {/* <Chip
                          label={(data.status?.toLowerCase()) === 'pending || process || complete ' ? 'Editable' : 'Locked'}
                          size="small"
                          icon={(data.status?.toLowerCase()) === 'pending' ? <EditIcon fontSize="small" /> : ""}
                          sx={{
                            background: (data.status?.toLowerCase()) === 'pending'
                              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                              : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                            color: 'white',
                            fontWeight: '500',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            height: '24px'
                          }}
                        /> */}
                        <Chip
                          label={data.status?.charAt(0).toUpperCase() + data.status?.slice(1).toLowerCase() || ''}
                          size="small"
                          sx={{
                            background: statusStyle.bg,
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '0.75rem',
                            height: '28px',
                            textTransform: 'capitalize',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            border: '1px solid rgba(255,255,255,0.2)'
                          }}
                        />

                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Enhanced Total Row */}
                  <TableRow sx={{
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(124, 58, 237, 0.08) 100%)',
                    '& .MuiTableCell-root': {
                      borderTop: '2px solid #8b5cf6',
                      py: 2
                    }
                  }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{
                          width: 28,
                          height: 28,
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          fontSize: '0.7rem',
                          fontWeight: '600'
                        }}>
                          Σ
                        </Avatar>
                        <Typography variant="subtitle2" fontWeight="600" fontSize="0.85rem">
                          Total Amount
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: 'white',
                        px: 1.5,
                        py: 1,
                        borderRadius: '8px',
                        display: 'inline-block',
                        boxShadow: '0 2px 10px rgba(245, 158, 11, 0.25)'
                      }}>
                        <Typography variant="subtitle2" fontWeight="600" fontSize="0.85rem">
                          ₹{parseFloat(calculateTotal()).toLocaleString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" color="textSecondary" fontWeight="500" fontSize="0.7rem">
                        Final Amount
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        p: 2.5,
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        borderTop: '1px solid rgba(139, 92, 246, 0.1)',
        gap: 1.5
      }}>
        <Button
          onClick={handleClose}
          startIcon={<CloseIcon fontSize="small" />}
          sx={{
            borderRadius: '8px',
            px: 2.5,
            py: 1,
            color: '#64748b',
            border: '1.5px solid #cbd5e1',
            fontSize: '0.8rem',
            fontWeight: '500',
            textTransform: 'none',
            '&:hover': {
              border: '1.5px solid #8b5cf6',
              background: 'rgba(139, 92, 246, 0.04)',
              color: '#8b5cf6'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={(data.status?.toLowerCase()) !== 'pending'}
          startIcon={<SaveIcon fontSize="small" />}
          sx={{
            borderRadius: '8px',
            px: 3,
            py: 1,
            background: (data.status?.toLowerCase()) === 'pending'
              ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
              : '#9ca3af',
            fontSize: '0.8rem',
            fontWeight: '500',
            textTransform: 'none',
            boxShadow: (data.status?.toLowerCase()) === 'pending'
              ? '0 4px 15px rgba(139, 92, 246, 0.3)'
              : 'none',
            '&:hover': (data.status?.toLowerCase()) === 'pending' ? {
              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              boxShadow: '0 6px 20px rgba(139, 92, 246, 0.4)',
              transform: 'translateY(-1px)'
            } : {},
            '&:disabled': {
              background: '#9ca3af',
              color: 'white',
              opacity: 0.6
            },
            transition: 'all 0.2s ease'
          }}
        >
          {(data.status?.toLowerCase()) === 'pending' ? 'Save Changes' : 'Save Disabled'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewRemittance;